import { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Row } from '@opendoor/atoms/box';
import { cn } from '@opendoor/atoms/utils';
import { useAuth } from '@opendoor/auth-fe/context';
import { getInitials } from 'components/landing-pages-v2/utils/getInitials';
import usePrevious from 'components/landing-pages-v2/utils/usePrevious';
import { useRouter } from 'next/router';

import { useObservability } from '../../../../../helpers/observability';
import MenuPanel from '../../menu/MenuPanel';
import { OpendoorLogo, OpendoorLogoBadge } from '../../OpendoorLogo';
import type { MegaNavItem, NavigationProps } from '../../types';
import { AccountSection } from '../AccountSection';
import { SkipLink } from '../SkipLink';
import { useLargerThanSM } from '../useLargerThanSM';
import { GLASS_HEADER_HEIGHT, redirectToLogin } from './constants';
import { GlassAccountSection } from './GlassAccountSection';
import { GlassMegaNav } from './GlassMegaNav';
import { GlassNavSearch } from './GlassNavSearch';

interface GlassHeaderProps extends NavigationProps {
  megaNavData?: MegaNavItem[];
}

const GlassHeader = ({
  slug,
  menuPanelNavLinks,
  mainNavLinks,
  ctaTitle,
  ctaLink,
  megaNavData,
}: GlassHeaderProps) => {
  const { pathname } = useRouter();
  const largerThanSM = useLargerThanSM();
  const { user: human, authentication } = useAuth();
  const { trackEvent } = useObservability();
  const isLoggedIn = authentication.state === 'authenticated';
  const initials = getInitials(human?.fullName, human?.firstName);
  const showLogin = !(pathname.includes('/login') || pathname.includes('/account'));

  const [isScrolled, setIsScrolled] = useState(false);
  const [isAddressHidden, setIsAddressHidden] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileSearchExpanded, setIsMobileSearchExpanded] = useState(false);
  const prevMobileMenuOpen = usePrevious(isMobileMenuOpen);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const addressObserverRef = useRef<IntersectionObserver | null>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let scrollHandler: (() => void) | null = null;
    let mutationObserver: MutationObserver | null = null;

    const rootMargin = `-${GLASS_HEADER_HEIGHT}px 0px 0px 0px`;

    const observeHero = (el: Element) => {
      observerRef.current?.disconnect();
      observerRef.current = new IntersectionObserver(
        ([entry]) => setIsScrolled(!entry.isIntersecting),
        { threshold: 0, rootMargin },
      );
      observerRef.current.observe(el);
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler);
        scrollHandler = null;
      }
    };

    const observeAddress = (el: Element) => {
      addressObserverRef.current?.disconnect();
      addressObserverRef.current = new IntersectionObserver(
        ([entry]) => setIsAddressHidden(!entry.isIntersecting),
        { threshold: 0, rootMargin },
      );
      addressObserverRef.current.observe(el);
    };

    const heroSelector = '[data-hero]';
    const addressSelector =
      '[class*="hp-reskin-v2-address-entry-wrapper"],.hp-reskin-v2-address-entry-wrapper';

    const tryObserve = () => {
      const heroEl = document.querySelector(heroSelector);
      if (heroEl) observeHero(heroEl);

      const addressEl = document.querySelector(addressSelector);
      if (addressEl) observeAddress(addressEl);

      return { heroFound: !!heroEl, addressFound: !!addressEl };
    };

    const { heroFound, addressFound } = tryObserve();

    if (!heroFound) {
      scrollHandler = () => setIsScrolled(window.scrollY > GLASS_HEADER_HEIGHT);
      window.addEventListener('scroll', scrollHandler, { passive: true });
    }

    if (!heroFound || !addressFound) {
      mutationObserver = new MutationObserver(() => {
        const result = tryObserve();
        if (result.heroFound && result.addressFound) {
          mutationObserver?.disconnect();
        }
      });
      mutationObserver.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      observerRef.current?.disconnect();
      addressObserverRef.current?.disconnect();
      mutationObserver?.disconnect();
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler);
      }
    };
  }, []);

  const handleMenuOpenChange = useCallback((itemName: string | null) => {
    setIsMenuOpen(itemName !== null);
  }, []);

  const handleMobileSearchExpandChange = useCallback((expanded: boolean) => {
    setIsMobileSearchExpanded(expanded);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => !prev);
  }, []);

  const trackMenuClick = useCallback(() => {
    trackEvent('cta-click', 'menu', undefined, { categoryOverride: 'homepage' });
  }, [trackEvent]);

  const trackAccountClick = useCallback(() => {
    trackEvent('cta-click', 'account-icon', undefined, { categoryOverride: 'homepage' });
  }, [trackEvent]);

  const exitComplete = () => {
    if (prevMobileMenuOpen === true) {
      const activeEl = document.activeElement;
      const isInputFocused =
        activeEl instanceof HTMLInputElement || activeEl instanceof HTMLTextAreaElement;
      if (!isInputFocused) {
        hamburgerRef.current?.focus();
      }
    }
  };

  useEffect(() => {
    if (largerThanSM && isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [largerThanSM, isMobileMenuOpen]);

  const anyMenuOpen = isMenuOpen || isMobileMenuOpen;

  // Desktop background/blur: transparent → translucent scrim when scrolled → opaque
  // scrim when a menu is open. Computed here to avoid a nested ternary in the JSX.
  let desktopHeaderBg = 'md:bg-transparent';
  if (anyMenuOpen) {
    desktopHeaderBg = 'md:bg-od-bg-scrim';
  } else if (isScrolled) {
    desktopHeaderBg = 'md:bg-od-bg-scrim/85 md:backdrop-blur-md';
  }
  // Mobile turns white once scrolled past the hero (the menu has its own panel).
  const mobileHeaderBg = isScrolled
    ? 'max-md:bg-od-bg-primary max-md:border-b-od-bg-scrim/6'
    : 'max-md:bg-transparent';

  return (
    <>
      {!largerThanSM && (
        <MenuPanel
          slug={slug}
          menuPanelNavLinks={menuPanelNavLinks}
          mainNavLinks={mainNavLinks}
          ctaTitle={ctaTitle}
          ctaLink={ctaLink}
          isVisible={isMobileMenuOpen}
          setIsVisible={setIsMobileMenuOpen}
          exitComplete={exitComplete}
          megaNavData={megaNavData}
          variant="glass"
        />
      )}

      <Box
        className={cn(
          'fixed inset-x-0 top-0 z-[2000] block h-18 w-full overflow-visible rounded-none border-b border-b-od-content-inverse-primary/8 backdrop-blur-xs transition-[background-color,backdrop-filter,border-color] duration-300',
          desktopHeaderBg,
          mobileHeaderBg,
        )}
      >
        <Box className="flex h-18 items-center justify-center">
          <Box className="grid h-18 w-full max-w-328 grid-cols-[auto_1fr_auto] items-center px-5">
            <SkipLink />

            <Row className="z-[5] col-start-1 col-end-2 row-start-1 row-end-2 items-center gap-4 pr-2">
              <Box
                href="/"
                analyticsName="cosmos-glass-nav-logo"
                aria-label="Go to Home page"
                className="flex items-center text-od-content-inverse-primary! max-md:hidden"
              >
                <OpendoorLogo width="96px" />
              </Box>
              <Box
                href="/"
                analyticsName="cosmos-glass-nav-logo"
                aria-label="Go to Home page"
                className={cn(
                  'items-center md:hidden',
                  isMobileSearchExpanded ? 'hidden' : 'flex',
                  isScrolled ? 'text-od-content-primary!' : 'text-od-content-inverse-primary!',
                )}
              >
                <OpendoorLogoBadge />
              </Box>
              <Box className="max-md:hidden">
                <GlassNavSearch isVisible={isAddressHidden} />
              </Box>
            </Row>

            <GlassMegaNav
              slug={slug}
              megaNavData={megaNavData}
              onOpenChange={handleMenuOpenChange}
            />

            <Box className="col-start-2 col-end-3 row-start-1 row-end-2 flex min-w-0 flex-1 items-center px-1 md:hidden">
              <GlassNavSearch
                isVisible={isAddressHidden && !isMobileMenuOpen}
                onExpandChange={handleMobileSearchExpandChange}
              />
            </Box>

            <Box className="row-start-1 row-end-2 [grid-column:-1/-2] flex shrink-0 items-center gap-4">
              <GlassAccountSection
                isLoggedIn={isLoggedIn}
                showLogin={showLogin}
                initials={initials}
              />

              <Box className={cn('pl-2 md:hidden', isMobileSearchExpanded ? 'hidden' : 'flex')}>
                <AccountSection
                  isLoggedIn={isLoggedIn}
                  showLogin={showLogin}
                  initials={initials}
                  redirectToLogin={redirectToLogin}
                  trackAccountClick={trackAccountClick}
                  trackMenuClick={trackMenuClick}
                  onMobileMenuToggle={toggleMobileMenu}
                  isMobileMenuVisible={isMobileMenuOpen}
                  hamburgerRef={hamburgerRef}
                  inverse={!isScrolled}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default GlassHeader;
